import { MasonryFlashList } from '@shopify/flash-list';
import { Box, Flex, useBreakpointValue } from 'native-base';
import React, { ReactNode, useMemo } from 'react';

import { NoContentContainer } from '~/components/no-content-container';
import { Spacing } from '~/components/spacing';
import { SystemMessage } from '~/components/system-message';

type Props<T> = {
    items: readonly T[];
    renderItem: (item: T) => ReactNode;
    noContentMessage?: string;
    customColumnCount?: number;
    minCardHeight?: number;
    minCardWidth?: number;
};

export const MasonryList = <T,>(props: Props<T>) => {
    const { items, noContentMessage, renderItem, customColumnCount, minCardHeight = 250, minCardWidth = 353 } = props;

    const breakpointColumnCount = useBreakpointValue({
        base: 1,
        md: 2,
        lg: 3
    });

    const columnsCount = useMemo((): number => {
        if (customColumnCount && customColumnCount < 4) {
            return customColumnCount;
        } else {
            return breakpointColumnCount;
        }
    }, [breakpointColumnCount, customColumnCount]);

    const listHeightEstimate = useMemo(() => {
        return (items.length * minCardHeight) / columnsCount;
    }, [columnsCount, items.length, minCardHeight]);

    const listWidthEstimate = useMemo(() => {
        return columnsCount > 1 ? minCardWidth * columnsCount : minCardWidth * 2;
    }, [columnsCount, minCardWidth]);

    const itemSizeEstimate = useMemo(() => {
        return columnsCount > 1 ? minCardWidth * columnsCount : minCardHeight * items.length;
    }, [columnsCount, items.length, minCardHeight, minCardWidth]);

    return (
        <MasonryFlashList
            key={`${columnsCount}`}
            keyExtractor={(_item, index) => `${index}`}
            scrollEnabled={false}
            data={items}
            estimatedItemSize={itemSizeEstimate === 0 ? minCardHeight : itemSizeEstimate}
            estimatedListSize={{
                height: listHeightEstimate === 0 ? minCardHeight : listHeightEstimate,
                width: listWidthEstimate === 0 ? minCardWidth : listWidthEstimate
            }}
            numColumns={columnsCount}
            renderItem={({ item }) => <Box m="2">{renderItem(item)}</Box>}
            ListEmptyComponent={
                <Flex>
                    <NoContentContainer>
                        <Spacing.Vertical.SMALL />
                        <SystemMessage label={noContentMessage ?? '-'} />
                    </NoContentContainer>
                </Flex>
            }
        />
    );
};
